import { UserAccessibleSection } from "../../openapi-typescript/common/user_accessible_section";
import { keysToCamelCase, toTitleCaseLabel } from "../global";
import { useUserRole } from "../state/user";
import { useUpdatables } from "./useUpdatable";
import { BreadcrumbItem } from "../../COREDesignSystem/Navigation/COREBreadcrumb";
import { FeatureFlag, useFeatureFlags } from "./useFeatureFlags";

export type UserAccessibleSectionWithBreadcrumbs = UserAccessibleSection & {
  breadcrumbs: BreadcrumbItem[];
};

// Searches for a section with the given path and returns it with breadcrumbs, checking child sections if not found at the top level.
export const findByPath = (
  userAccessibleSections: UserAccessibleSection[],
  path: string,
  breadcrumbsParent: BreadcrumbItem[] = []
): UserAccessibleSectionWithBreadcrumbs[] => {
  for (const {
    callToActionUrl,
    title,
    children,
    ...rest
  } of userAccessibleSections) {
    const isMatch = callToActionUrl === path;
    const breadcrumbs = [
      ...breadcrumbsParent,
      { title, ...(!isMatch && { href: callToActionUrl }) },
    ];

    if (isMatch) {
      return [{ ...rest, callToActionUrl, children, title, breadcrumbs }];
    }

    const foundItem = children?.length
      ? findByPath(children, path, breadcrumbs)
      : [];
    if (foundItem.length) return foundItem;
  }

  return [];
};

// Dashboards are surfaced as feature cards whose title is the user's own
// dashboard name, so their casing must be left exactly as the user typed it.
const isDashboardSection = (section: UserAccessibleSection): boolean =>
  section.callToActionUrl.startsWith("/dashboard/");

// Feature card standing data is stored in sentence case ("Markets admin"), but
// section titles are displayed as title case in the menu, cards, breadcrumbs,
// page headers and browser tab title.
export const titleCaseSectionTitles = (
  sections: UserAccessibleSection[]
): UserAccessibleSection[] =>
  sections.map((section) => ({
    ...section,
    title: isDashboardSection(section)
      ? section.title
      : toTitleCaseLabel(section.title),
    children: titleCaseSectionTitles(section.children ?? []),
  }));

export const useUserAccessibleSections = () => {
  const isPendingRole = useUserRole() === "pending";
  const { features } = useFeatureFlags();
  const { updatable, ...other } = useUpdatables<UserAccessibleSection>({
    get: ["userAccessibleSections", {}, isPendingRole],
    transformToRichTypes: (record) => keysToCamelCase(record),
  });

  let sections = filterPreviewPages(
    updatable.map(({ data }) => data) as UserAccessibleSection[]
  );

  if (features.includes("asset-intelligence-admin-centre" as FeatureFlag)) {
    sections = sections.map((section) => {
      if (section.title === "Asset Intelligence") {
        return {
          ...section,
          children: section.children.map((child) => {
            if (child.title === "Admin Centre") {
              const createAssetCard: UserAccessibleSection = {
                title: "Add Asset",
                subtitle: "Manually create a new asset",
                icon: "circle-plus",
                callToActionUrl: "/asset-intelligence/admin-centre/add-asset",
                children: [],
                featureFlag: "asset-intelligence-admin-centre" as FeatureFlag,
                isShownInMenu: true,
                sortOrder: 999,
              };
              return {
                ...child,
                children: [...child.children, createAssetCard],
              };
            }
            return child;
          }),
        };
      }
      return section;
    });
  }

  return {
    userAccessibleSections: titleCaseSectionTitles(sections),
    ...other,
  };
};

export const filterPreviewPages = (
  data: UserAccessibleSection[]
): UserAccessibleSection[] => {
  return data
    ?.filter((item) => {
      if (item?.featureFlag?.endsWith("-preview")) {
        const mainValue = item.featureFlag.replace("-preview", "");
        return !data.some((item) => item.featureFlag === mainValue);
      }
      return true;
    })
    .map((item) => {
      if (item?.featureFlag?.endsWith("-preview")) {
        const mainValue = item.title.replace(" (Preview)", "");
        return {
          ...item,
          title: mainValue,
          children: filterPreviewPages(item.children),
        };
      }
      return { ...item, children: filterPreviewPages(item.children) };
    });
};
